import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { GitMerge, Loader2, Crown } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const fmt = (v) => (Array.isArray(v) ? v.join(", ") : v == null || v === "" ? "" : String(v));

// Choose a survivor + resolve field conflicts, then propose a merge (goes to Review Queue).
export const MergeDialog = ({ open, onOpenChange, type, typeLabel, fields, members, onProposed }) => {
  const [survivorId, setSurvivorId] = useState("");
  const [choices, setChoices] = useState({}); // fieldKey -> memberId
  const [saving, setSaving] = useState(false);

  const memberById = useMemo(() => Object.fromEntries((members || []).map((m) => [m.id, m])), [members]);

  useEffect(() => {
    if (!open || !members?.length) return;
    const sid = members[0].id;
    setSurvivorId(sid);
    const c = {};
    (fields || []).forEach((f) => {
      const withVal = members.find((m) => fmt(m.data?.[f.key]));
      c[f.key] = fmt(memberById[sid]?.data?.[f.key]) ? sid : (withVal?.id || sid);
    });
    setChoices(c);
  }, [open, members, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const conflictFields = (fields || []).filter((f) => members?.some((m) => fmt(m.data?.[f.key])));

  const resolved = useMemo(() => {
    const out = {};
    (fields || []).forEach((f) => {
      const src = memberById[choices[f.key]];
      const val = src?.data?.[f.key];
      if (val !== undefined && val !== null && val !== "") out[f.key] = val;
    });
    return out;
  }, [choices, fields, memberById]);

  const submit = async () => {
    const dupIds = members.filter((m) => m.id !== survivorId).map((m) => m.id);
    if (dupIds.length === 0) { toast.error("Nothing to merge into the survivor"); return; }
    setSaving(true);
    try {
      await api.post("/kg/merges", { type, survivor_id: survivorId, duplicate_ids: dupIds, resolved_data: resolved });
      toast.success("Merge proposed — awaiting review by another curator or admin");
      onOpenChange(false);
      onProposed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't propose the merge");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-primary" /> Merge {typeLabel || type} entities
          </DialogTitle>
          <DialogDescription>
            Pick the entity to keep (the survivor). Others are archived and all their relationships, notes and progress move to the survivor. Choose the winning value for each field.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-5">
          <div>
            <div className="text-sm font-semibold text-foreground mb-2">Survivor (kept)</div>
            <div className="grid gap-2">
              {members?.map((m) => (
                <button key={m.id} onClick={() => setSurvivorId(m.id)}
                  className={`flex items-center gap-2 text-left rounded-md border px-3 py-2 text-sm transition-colors ${
                    survivorId === m.id ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"
                  }`}
                  data-testid={`merge-survivor-${m.id}`}>
                  <Crown className={`w-4 h-4 shrink-0 ${survivorId === m.id ? "text-primary" : "text-muted-foreground/40"}`} />
                  <span className="font-medium text-foreground">{m.label}</span>
                  <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                  <span className="text-xs text-muted-foreground ml-auto truncate">{m.created_by || ""}</span>
                </button>
              ))}
            </div>
          </div>

          {conflictFields.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-foreground mb-2">Field values</div>
              <div className="space-y-3">
                {conflictFields.map((f) => (
                  <div key={f.key} className="rounded-md border border-border p-3" data-testid={`merge-field-${f.key}`}>
                    <div className="text-xs font-semibold text-muted-foreground mb-1.5">{f.label || f.key}</div>
                    <div className="grid gap-1.5">
                      {members.map((m) => {
                        const val = fmt(m.data?.[f.key]);
                        return (
                          <label key={m.id} className={`flex items-start gap-2 text-sm rounded px-2 py-1.5 cursor-pointer ${
                            choices[f.key] === m.id ? "bg-primary/10" : "hover:bg-secondary/40"
                          }`} data-testid={`merge-field-${f.key}-opt-${m.id}`}>
                            <input type="radio" name={`field-${f.key}`} checked={choices[f.key] === m.id}
                              onChange={() => setChoices((c) => ({ ...c, [f.key]: m.id }))} className="mt-1 accent-[#ffb020]" />
                            <span className={val ? "text-foreground" : "text-muted-foreground italic"}>
                              {val || "(empty)"}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="merge-propose-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Propose merge ({members?.length ? members.length - 1 : 0} → 1)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
